import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Badge, Button, Card, Col, Form, ProgressBar, Row, Spinner, Table,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotate, faSync, faGavel, faTriangleExclamation, faSearch, faEye, faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import PaypalDisputeModal from "./PaypalDisputeModal";

const ACCOUNTS_STORAGE_KEY = "paypal_disputes_selected_accounts";
const PAGE_SIZE = 25;

const STATE_VARIANT = {
  REQUIRED_ACTION: "danger",
  UNDER_PAYPAL_REVIEW: "warning",
  RESOLVED: "success",
  CLOSED: "secondary",
  WAITING_FOR_BUYER_RESPONSE: "info",
  OPEN_INQUIRIES: "primary",
};

// Outcome mapping. Once PayPal sets ANY outcome the dispute is closed and
// money has either stayed with the seller (Won) or moved to the buyer (Lost).
// Pending = no outcome yet.
const OUTCOME_LABEL = {
  // Seller kept the money.
  RESOLVED_SELLER_FAVOUR: { label: "Won", variant: "success" },
  RESOLVED_SELLER_FAVOR: { label: "Won", variant: "success" },
  DENIED: { label: "Won", variant: "success" },
  CANCELED_BY_BUYER: { label: "Won", variant: "success" },

  // Money went out (full / partial / payout / accepted claim).
  RESOLVED_BUYER_FAVOUR: { label: "Lost", variant: "danger" },
  RESOLVED_BUYER_FAVOR: { label: "Lost", variant: "danger" },
  RESOLVED_WITH_REFUND: { label: "Lost", variant: "danger" },
  RESOLVED_WITH_PAYOUT: { label: "Lost", variant: "danger" },
  PARTIAL_REFUND: { label: "Lost", variant: "danger" },
  ACCEPTED: { label: "Lost", variant: "danger" },

  // Still pending PayPal's verdict.
  NONE: { label: "Đang xử lý", variant: "info" },
  "": { label: "—", variant: "light" },
};

// Sets used to classify rollups consistently across summary + per-account
// stats. Anything outside these sets with an outcome present is treated as
// Lost (seller paid out) — the safe default per PayPal semantics.
const WON_OUTCOMES = new Set([
  "RESOLVED_SELLER_FAVOUR", "RESOLVED_SELLER_FAVOR",
  "DENIED", "CANCELED_BY_BUYER",
]);
const LOST_OUTCOMES = new Set([
  "RESOLVED_BUYER_FAVOUR", "RESOLVED_BUYER_FAVOR",
  "RESOLVED_WITH_REFUND", "RESOLVED_WITH_PAYOUT", "PARTIAL_REFUND",
  "ACCEPTED",
]);
const PENDING_OUTCOMES = new Set(["NONE", ""]);

const fmtAmount = (amt) => {
  if (!amt?.value) return "—";
  return `${amt.value} ${amt.currency_code || ""}`.trim();
};
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString();
};

const PaypalDisputes = ({ accountList = [] }) => {
  const [accountIds, setAccountIds] = useState(() => {
    try {
      const raw = localStorage.getItem(ACCOUNTS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });

  // Sync with available accounts.
  useEffect(() => {
    if (!accountList?.length) return;
    const valid = accountList.map((a) => a._id);
    const filtered = accountIds.filter((id) => valid.includes(id));
    if (filtered.length !== accountIds.length) {
      setAccountIds(filtered.length ? filtered : valid);
    } else if (!accountIds.length) {
      setAccountIds(valid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountList]);

  useEffect(() => {
    try {
      if (accountIds.length) {
        localStorage.setItem(ACCOUNTS_STORAGE_KEY, JSON.stringify(accountIds));
      } else {
        localStorage.removeItem(ACCOUNTS_STORAGE_KEY);
      }
    } catch { /* ignore */ }
  }, [accountIds]);

  const accountById = useMemo(() => {
    const map = {};
    (accountList || []).forEach((a) => { map[a._id] = a; });
    return map;
  }, [accountList]);

  const toggleAccount = (id) => {
    setAccountIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAll = () => {
    if (!accountList.length) return;
    if (accountIds.length === accountList.length) setAccountIds([]);
    else setAccountIds(accountList.map((a) => a._id));
  };

  /* ---------------- Sync ---------------- */
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0, message: "" });
  const [days, setDays] = useState(180);
  const [wiping, setWiping] = useState(false);
  const cancelRef = useRef(false);

  /* ---------------- List + filters ---------------- */
  const [statusFilter, setStatusFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(false);

  /* ---------------- Stats ---------------- */
  const [stats, setStats] = useState({ byAccount: [], grand: { total: 0, outcomes: {} } });
  const [loadingStats, setLoadingStats] = useState(false);

  /* ---------------- Detail modal ---------------- */
  const [modalDispute, setModalDispute] = useState(null);

  const buildAccountsParam = useCallback(() => accountIds.join(","), [accountIds]);

  const fetchList = useCallback(async (p = 1) => {
    setLoadingList(true);
    try {
      const r = await Utils._apiRequest({
        path: Enums.endpoints.paypal.DISPUTES_LIST,
        type: "GET",
        query: {
          accountIds: buildAccountsParam(),
          status: statusFilter,
          stage: stageFilter,
          outcome: outcomeFilter,
          search,
          page: p,
          limit: PAGE_SIZE,
        },
      });
      if (!r?.ok) throw new Error(r?.errors || "Không tải được danh sách");
      setRows(r.items || []);
      setTotal(r.total || 0);
      setPage(r.page || p);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingList(false);
    }
  }, [buildAccountsParam, statusFilter, stageFilter, outcomeFilter, search]);

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const r = await Utils._apiRequest({
        path: Enums.endpoints.paypal.DISPUTES_STATS,
        type: "GET",
        query: { accountIds: buildAccountsParam() },
      });
      if (r?.ok) setStats({ byAccount: r.byAccount || [], grand: r.grand || { total: 0, outcomes: {} } });
    } catch (err) {
      console.log("stats:", err.message);
    } finally {
      setLoadingStats(false);
    }
  }, [buildAccountsParam]);

  // Initial load + on filter change.
  useEffect(() => {
    if (!accountIds.length) { setRows([]); setTotal(0); setStats({ byAccount: [], grand: { total: 0, outcomes: {} } }); return; }
    fetchList(1);
    fetchStats();
  }, [accountIds, statusFilter, stageFilter, outcomeFilter]); // eslint-disable-line

  const handleSearch = (e) => {
    e?.preventDefault();
    fetchList(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const runWipe = async () => {
    if (!accountIds.length) { toast.warning("Chọn ít nhất 1 tài khoản"); return; }
    const labels = accountIds.map((id) => accountById[id]?.accountName || id).join(", ");
    if (!window.confirm(
      `Xóa toàn bộ disputes trong DB của:\n${labels}\n\nThao tác này KHÔNG ảnh hưởng PayPal — chỉ xóa bản ghi local. Tiếp tục?`,
    )) return;
    setWiping(true);
    try {
      const r = await Utils._apiRequest({
        path: Enums.endpoints.paypal.DISPUTES_DELETE,
        type: "DELETE",
        payload: { accountIds },
      });
      if (!r?.ok) throw new Error(r?.errors || "Xóa thất bại");
      toast.success(`Đã xóa ${r.deleted} disputes`);
      fetchList(1);
      fetchStats();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setWiping(false);
    }
  };

  const runSync = async () => {
    if (!accountIds.length) { toast.warning("Chọn ít nhất 1 tài khoản"); return; }
    setSyncing(true);
    cancelRef.current = false;
    setSyncProgress({ current: 0, total: accountIds.length, message: "Đang khởi tạo…" });

    let totalUpserted = 0;
    let totalFailed = 0;
    try {
      for (let i = 0; i < accountIds.length; i++) {
        if (cancelRef.current) break;
        const accId = accountIds[i];
        const acc = accountById[accId];
        const accLabel = acc ? `${acc.accountName}` : accId;
        setSyncProgress({
          current: i,
          total: accountIds.length,
          message: `[${i + 1}/${accountIds.length}] ${accLabel} — đang tải…`,
        });
        // eslint-disable-next-line no-await-in-loop
        const r = await Utils._apiRequest({
          path: Enums.endpoints.paypal.DISPUTES_SYNC,
          type: "POST",
          payload: { accountId: accId, days },
        });
        if (!r?.ok) throw new Error(r?.errors || `Sync thất bại (${accLabel})`);
        totalUpserted += r.upserted || 0;
        totalFailed += r.failed || 0;
        setSyncProgress({
          current: i + 1,
          total: accountIds.length,
          message: `[${i + 1}/${accountIds.length}] ${accLabel} — ${r.upserted}/${r.total} disputes`,
        });
      }
      toast.success(`Sync xong • ${totalUpserted} disputes${totalFailed ? `, ${totalFailed} lỗi` : ""}`);
      // Refresh list + stats
      fetchList(1);
      fetchStats();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleRowAction = () => {
    // After any modal action, refresh the row + stats.
    fetchList(page);
    fetchStats();
  };

  const renderOutcomeBadge = (code) => {
    // Truly empty / not yet resolved → show muted dash so user can tell apart
    // from explicit NONE. Tooltip gives the raw code for production debugging.
    if (!code) {
      return <span className="text-muted" title="Chưa có outcome (dispute đang mở)">—</span>;
    }
    const def = OUTCOME_LABEL[code];
    if (def) {
      return <Badge bg={def.variant} title={code}>{def.label}</Badge>;
    }
    // Unknown code from PayPal → render raw so it's still actionable.
    return <Badge bg="secondary" title={code}>{code}</Badge>;
  };
  const renderStateBadge = (state) => (
    <Badge bg={STATE_VARIANT[state] || "secondary"}>{state || "—"}</Badge>
  );

  /* ---------------- Render ---------------- */
  return (
    <div className="my-3">
      <Card className="mb-3">
        <Card.Header className="d-flex align-items-center">
          <FontAwesomeIcon icon={faGavel} className="me-2" />
          <strong>Quản lý Disputes</strong>
          <Badge bg="secondary" className="ms-2">{total}</Badge>
        </Card.Header>
        <Card.Body>
          {accountList.length === 0 && (
            <Alert variant="warning" className="py-2 mb-3">
              <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
              Chưa có tài khoản PayPal. Hãy thêm tài khoản ở trên trước.
            </Alert>
          )}

          {/* Sync controls */}
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Group>
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label className="small mb-1">
                    Tài khoản{" "}
                    <span className="text-muted">({accountIds.length}/{accountList.length})</span>
                  </Form.Label>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 small"
                    onClick={toggleSelectAll}
                    disabled={!accountList.length || syncing}
                  >
                    {accountIds.length === accountList.length ? "Bỏ chọn tất cả" : "Chọn tất cả"}
                  </Button>
                </div>
                <div
                  className="border rounded p-2"
                  style={{ maxHeight: 110, overflowY: "auto", background: "#fff" }}
                >
                  {accountList.length === 0 && <div className="small text-muted">Chưa có tài khoản</div>}
                  {accountList.map((a) => (
                    <Form.Check
                      key={a._id}
                      type="checkbox"
                      id={`pp-disp-acc-${a._id}`}
                      className="small"
                      checked={accountIds.includes(a._id)}
                      onChange={() => toggleAccount(a._id)}
                      disabled={syncing}
                      label={`${a.accountName} (${a.meta?.mode || "live"})`}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small">Lookback (ngày)</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  min={1}
                  max={180}
                  value={days}
                  onChange={(e) => setDays(parseInt(e.target.value, 10) || 0)}
                  disabled={syncing}
                />
                <Form.Text className="text-muted small">PayPal giới hạn 180 ngày</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  className="flex-grow-1"
                  onClick={runSync}
                  disabled={!accountIds.length || syncing || wiping || days <= 0}
                >
                  {syncing ? (
                    <><Spinner animation="border" size="sm" className="me-2" /> Đang sync…</>
                  ) : (
                    <><FontAwesomeIcon icon={faSync} className="me-2" /> Sync Disputes</>
                  )}
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={runWipe}
                  disabled={!accountIds.length || syncing || wiping}
                  title="Xóa toàn bộ dispute trong DB của các account đã chọn (không ảnh hưởng PayPal)"
                >
                  {wiping ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faTrash} className="me-1" /> Xóa DB</>}
                </Button>
              </div>
              {syncing && (
                <ProgressBar
                  className="mt-2"
                  now={syncProgress.total ? (syncProgress.current / syncProgress.total) * 100 : 0}
                  label={`${syncProgress.current}/${syncProgress.total}`}
                />
              )}
              {syncing && (
                <div className="small text-muted mt-1">{syncProgress.message}</div>
              )}
            </Col>
          </Row>

          {/* Grand summary on its own row */}
          <Row className="mt-3">
            <Col xs={12}>
              <div className="small d-flex flex-wrap align-items-center gap-3 p-2 bg-light rounded">
                <span><strong>Tổng kết:</strong> {stats.grand.total} disputes</span>
                {(() => {
                  // Roll up every outcome into 4 buckets: Won / Lost / Đang xử lý / Khác.
                  const buckets = { Won: 0, Lost: 0, Pending: 0, Other: 0 };
                  Object.entries(stats.grand.outcomes || {}).forEach(([k, v]) => {
                    const n = v || 0;
                    if (WON_OUTCOMES.has(k)) buckets.Won += n;
                    else if (LOST_OUTCOMES.has(k)) buckets.Lost += n;
                    else if (PENDING_OUTCOMES.has(k)) buckets.Pending += n;
                    else buckets.Other += n;
                  });
                  const items = [
                    { key: "Won", label: "Won", variant: "success", count: buckets.Won },
                    { key: "Lost", label: "Lost", variant: "danger", count: buckets.Lost },
                    { key: "Pending", label: "Đang xử lý", variant: "info", count: buckets.Pending },
                    { key: "Other", label: "Khác", variant: "secondary", count: buckets.Other },
                  ].filter((b) => b.count > 0);
                  return (
                    <>
                      {items.length > 0 && <span className="text-muted">|</span>}
                      {items.map((b) => (
                        <span key={b.key} className="d-inline-flex align-items-center gap-1">
                          <Badge bg={b.variant}>{b.label}</Badge>
                          <strong>{b.count}</strong>
                        </span>
                      ))}
                    </>
                  );
                })()}
              </div>
            </Col>
          </Row>

          {/* Per-account stats */}
          {stats.byAccount.length > 0 && (
            <div className="mt-3">
              <Table size="sm" bordered className="mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>Tài khoản</th>
                    <th>Tổng</th>
                    <th>Won</th>
                    <th>Lost</th>
                    <th>Đang xử lý</th>
                    <th>Khác</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.byAccount.map((row) => {
                    const outcomes = row.outcomes || {};
                    let won = 0;
                    let lost = 0;
                    let pending = 0;
                    let other = 0;
                    Object.entries(outcomes).forEach(([k, v]) => {
                      const n = v || 0;
                      if (WON_OUTCOMES.has(k)) won += n;
                      else if (LOST_OUTCOMES.has(k)) lost += n;
                      else if (PENDING_OUTCOMES.has(k)) pending += n;
                      else other += n;
                    });
                    return (
                      <tr key={row.accountId}>
                        <td>{row.accountName} <span className="text-muted">({row.mode})</span></td>
                        <td>{row.total}</td>
                        <td className="text-success">{won}</td>
                        <td className="text-danger">{lost}</td>
                        <td className="text-info">{pending}</td>
                        <td>{other}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          <hr />

          {/* Filters */}
          <Form onSubmit={handleSearch}>
            <Row className="g-2 mb-2">
              <Col md={3}>
                <Form.Control
                  size="sm"
                  placeholder="Tìm dispute id / transaction / buyer email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Form.Select size="sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">Status: Tất cả</option>
                  <option value="OPEN">OPEN</option>
                  <option value="WAITING_FOR_BUYER_RESPONSE">WAITING_FOR_BUYER_RESPONSE</option>
                  <option value="WAITING_FOR_SELLER_RESPONSE">WAITING_FOR_SELLER_RESPONSE</option>
                  <option value="UNDER_REVIEW">UNDER_REVIEW</option>
                  <option value="RESOLVED">RESOLVED</option>
                  <option value="OTHER">OTHER</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Select size="sm" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
                  <option value="">Stage: Tất cả</option>
                  <option value="INQUIRY">INQUIRY</option>
                  <option value="CHARGEBACK">CHARGEBACK</option>
                  <option value="PRE_ARBITRATION">PRE_ARBITRATION</option>
                  <option value="ARBITRATION">ARBITRATION</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Select size="sm" value={outcomeFilter} onChange={(e) => setOutcomeFilter(e.target.value)}>
                  <option value="">Outcome: Tất cả</option>
                  <option value="RESOLVED_SELLER_FAVOUR">Won (Seller favour)</option>
                  <option value="RESOLVED_BUYER_FAVOUR">Lost (Buyer favour)</option>
                  <option value="RESOLVED_WITH_REFUND">Refunded</option>
                  <option value="CANCELED_BY_BUYER">Canceled by buyer</option>
                  <option value="ACCEPTED">Accepted</option>
                  <option value="DENIED">Denied</option>
                </Form.Select>
              </Col>
              <Col md={3} className="d-flex gap-2">
                <Button type="submit" size="sm" variant="primary" className="flex-grow-1">
                  <FontAwesomeIcon icon={faSearch} className="me-1" /> Tìm
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => fetchList(page)} disabled={loadingList}>
                  <FontAwesomeIcon icon={faRotate} />
                </Button>
              </Col>
            </Row>
          </Form>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <Table striped hover size="sm" className="mb-0 small align-middle">
              <thead className="table-light">
                <tr>
                  <th>Dispute ID</th>
                  <th>Account</th>
                  <th>Transaction</th>
                  <th>Amount</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Stage</th>
                  <th>Outcome</th>
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loadingList && (
                  <tr><td colSpan={10} className="text-center py-3"><Spinner animation="border" size="sm" /> Đang tải…</td></tr>
                )}
                {!loadingList && rows.length === 0 && (
                  <tr><td colSpan={10} className="text-center text-muted py-3">Chưa có dispute nào. Hãy bấm "Sync Disputes".</td></tr>
                )}
                {!loadingList && rows.map((d) => (
                  <tr key={d.disputeId}>
                    <td><code>{d.disputeId}</code></td>
                    <td>
                      {d.account ? (
                        <span>
                          {d.account.accountName}
                          <br />
                          <span className="text-muted small">{d.account.account}</span>
                        </span>
                      ) : "—"}
                    </td>
                    <td>
                      <code className="small">{d.primaryTransactionId || "—"}</code>
                      {d.primaryInvoiceNumber && <div className="text-muted small">Inv: {d.primaryInvoiceNumber}</div>}
                    </td>
                    <td>{fmtAmount(d.amount)}</td>
                    <td><span className="text-muted small">{d.reason || "—"}</span></td>
                    <td>{renderStateBadge(d.disputeState || d.status)}</td>
                    <td><Badge bg="light" text="dark">{d.disputeLifeCycleStage || "—"}</Badge></td>
                    <td>{renderOutcomeBadge(d.outcome?.outcome_code)}</td>
                    <td className="small">{fmtDate(d.updateTime || d.createTime)}</td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => setModalDispute(d)}>
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <span className="small text-muted">Trang {page} / {totalPages} • {total} disputes</span>
              <div className="d-flex gap-1">
                <Button size="sm" variant="outline-secondary" disabled={page <= 1 || loadingList} onClick={() => fetchList(page - 1)}>← Prev</Button>
                <Button size="sm" variant="outline-secondary" disabled={page >= totalPages || loadingList} onClick={() => fetchList(page + 1)}>Next →</Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Detail / actions modal */}
      <PaypalDisputeModal
        dispute={modalDispute}
        onClose={() => setModalDispute(null)}
        onActionDone={handleRowAction}
      />
    </div>
  );
};

export default PaypalDisputes;
